import React, { useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react'
import useMessageStore from '../stores/messageStore'
import logger from '../utils/logger';

const Contact = () => {
  const { t } = useTranslation()
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  // Get message store action
  const sendMessage = useMessageStore((state) => state.sendMessage)

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  })
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  // Validation rules
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) {
          return 'Name is required'
        }
        if (value.trim().length < 2) {
          return 'Name must be at least 2 characters'
        }
        if (value.trim().length > 50) {
          return 'Name must not exceed 50 characters'
        }
        if (!/^[a-zA-Z\s.'-]+$/.test(value)) {
          return 'Name can only contain letters, spaces, dots, hyphens and apostrophes'
        }
        return ''

      case 'email':
        if (!value.trim()) {
          return 'Email is required'
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(value)) {
          return 'Please enter a valid email address'
        }
        return ''

      case 'phone':
        if (value && value.trim()) {
          // Remove spaces, dashes, and brackets for validation
          const cleanPhone = value.replace(/[\s\-()]/g, '')
          if (!/^[+]?[\d]{10,15}$/.test(cleanPhone)) {
            return 'Phone number must be 10-15 digits'
          }
        }
        return ''

      case 'subject':
        if (!value) {
          return 'Please select a subject'
        }
        return ''

      case 'message':
        if (!value.trim()) {
          return 'Message is required'
        }
        if (value.trim().length < 10) {
          return 'Message must be at least 10 characters'
        }
        if (value.trim().length > 1000) {
          return 'Message must not exceed 1000 characters'
        }
        return ''

      default:
        return ''
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    
    setFormData({
      ...formData,
      [name]: value
    })

    // Validate field if it has been touched
    if (touched[name]) {
      const error = validateField(name, value)
      setErrors({
        ...errors,
        [name]: error
      })
    }
  }

  const handleBlur = (e) => {
    const { name, value } = e.target
    
    setTouched({
      ...touched,
      [name]: true
    })

    const error = validateField(name, value)
    setErrors({
      ...errors,
      [name]: error
    })
  }

  const validateForm = () => {
    const newErrors = {}
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key])
      if (error) {
        newErrors[key] = error
      }
    })
    return newErrors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true
      return acc
    }, {})
    setTouched(allTouched)

    // Validate all fields
    const formErrors = validateForm()
    setErrors(formErrors)

    // Check if there are any errors
    if (Object.keys(formErrors).length > 0) {
      // Scroll to first error
      const firstErrorField = Object.keys(formErrors)[0]
      const errorElement = document.getElementById(firstErrorField)
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        errorElement.focus()
      }
      return
    }

    setIsSubmitting(true)

    try {
      // Determine message type based on subject or content
      let messageType = 'General Inquiry'
      const subjectLower = formData.subject.toLowerCase()
      const messageLower = formData.message.toLowerCase()

      if (subjectLower.includes('admission') || messageLower.includes('admission')) {
        messageType = 'Admission Inquiry'
      } else if (subjectLower.includes('fee') || messageLower.includes('fee') || messageLower.includes('payment')) {
        messageType = 'Fee Inquiry'
      } else if (subjectLower.includes('transport') || messageLower.includes('bus') || messageLower.includes('transport')) {
        messageType = 'Transportation'
      } else if (subjectLower.includes('academic') || messageLower.includes('curriculum') || messageLower.includes('study')) {
        messageType = 'Academic Inquiry'
      } else if (subjectLower.includes('event') || messageLower.includes('activity') || messageLower.includes('program')) {
        messageType = 'Events & Activities'
      }

      // Send message via store
      const result = await sendMessage({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        type: messageType
      })

      if (result.success) {
        setIsSubmitting(false)
        setIsSubmitted(true)
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        })
        setErrors({})
        setTouched({})

        // Reset success message after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false)
        }, 5000)
      } else {
        throw new Error(result.message || 'Failed to send message')
      }

    } catch (error) {
      logger.error('Error submitting message:', error)
      alert(error.message || 'Failed to send message. Please try again.')
      setIsSubmitting(false)
    }
  }

  const contactInfo = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: t('contact.address'),
      details: [
        "DR. HARIBHAI GODHANI CAMPUS",
        "MANAGED BY SHREE SARDAR PATEL EDUCATION TRUST",
        "B/H. RAILWAY STATION, SARDARPARA,",
        "JOSHIPURA, JUNAGADH 362001",
        "Gujarat, India"
      ]
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: t('contact.phone'),
      details: [
        "+91 94278 05272",
        "Office Hours: 9:00 AM - 5:00 PM"
      ]
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: t('contact.email'),
      details: [
        "info@sardarpateleducationtrust.org",
        "dr.haribhaigodhanicampusjnd@gmail.com"
      ]
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: t('contact.hours'),
      details: [
        t('contact.mondayFriday'),
        t('contact.saturday'),
        t('contact.sunday')
      ]
    }
  ]

  return (
    <section id="contact" className="py-10 sm:py-12 md:py-16 lg:py-20 xl:py-24 bg-gradient-to-br from-primary-light via-primary-mid/30 to-primary-light/50 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 max-w-7xl">
        <div
          ref={ref}
          className={`transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
        >
          {/* Section Header */}
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-4xl xl:text-5xl font-bold text-primary-dark font-poppins mb-2 sm:mb-3 tracking-tight">
              {t('contact.title')}
            </h2>
            <div className="w-16 sm:w-20 h-1 bg-gradient-to-r from-primary-dark to-primary-mid mx-auto mb-3 sm:mb-4 rounded-full"></div>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed px-2">
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10">
            {/* Contact Form */}
            <div className="bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300 p-4 sm:p-6 lg:p-8 h-full border border-gray-100">
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-primary-dark mb-4 sm:mb-5 font-montserrat tracking-tight">
                {t('contact.title')}
              </h3>

              {isSubmitted && (
                <div className="mb-5 p-3.5 bg-green-50 border border-green-200 rounded-xl flex items-center space-x-3 shadow-sm">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <p className="text-green-700 font-medium text-sm">
                    {t('contact.successMessage')}
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-gray-700 font-semibold text-sm mb-2">
                      {t('contact.fullName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 bg-gray-50 focus:bg-white transition-all duration-200 text-gray-800 placeholder:text-gray-400 ${
                        errors.name && touched.name
                          ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500'
                          : 'border-gray-300 focus:ring-primary-dark/50 focus:border-primary-dark'
                      }`}
                      placeholder={t('contact.placeholders.enterName')}
                      maxLength="50"
                    />
                    {errors.name && touched.name && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1.5 flex-shrink-0" /> {errors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-semibold text-sm mb-2">
                      {t('contact.emailAddress')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 bg-gray-50 focus:bg-white transition-all duration-200 text-gray-800 placeholder:text-gray-400 ${
                        errors.email && touched.email
                          ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500'
                          : 'border-gray-300 focus:ring-primary-dark/50 focus:border-primary-dark'
                      }`}
                      placeholder={t('contact.placeholders.enterEmail')}
                    />
                    {errors.email && touched.email && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1.5 flex-shrink-0" /> {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-semibold text-sm mb-2">
                      {t('contact.phoneNumber')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 bg-gray-50 focus:bg-white transition-all duration-200 text-gray-800 placeholder:text-gray-400 ${
                        errors.phone && touched.phone
                          ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500'
                          : 'border-gray-300 focus:ring-primary-dark/50 focus:border-primary-dark'
                      }`}
                      placeholder={t('contact.placeholders.enterPhone')}
                    />
                    {errors.phone && touched.phone && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1.5 flex-shrink-0" /> {errors.phone}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-gray-700 font-semibold text-sm mb-2">
                      {t('contact.subject')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 bg-gray-50 focus:bg-white transition-all duration-200 text-gray-800 cursor-pointer ${
                        errors.subject && touched.subject
                          ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500'
                          : 'border-gray-300 focus:ring-primary-dark/50 focus:border-primary-dark'
                      }`}
                    >
                      <option value="">{t('contact.subjects.selectSubject')}</option>
                      <option value="admission">{t('contact.subjects.admission')}</option>
                      <option value="academic">{t('contact.subjects.academic')}</option>
                      <option value="facilities">{t('contact.subjects.facilities')}</option>
                      <option value="transport">{t('contact.subjects.transport')}</option>
                      <option value="general">{t('contact.subjects.general')}</option>
                      <option value="complaint">{t('contact.subjects.complaint')}</option>
                    </select>
                    {errors.subject && touched.subject && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1.5 flex-shrink-0" /> {errors.subject}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-gray-700 font-semibold text-sm mb-2">
                    {t('contact.message')} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    rows={4}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 bg-gray-50 focus:bg-white transition-all duration-200 resize-vertical text-gray-800 placeholder:text-gray-400 ${
                      errors.message && touched.message
                        ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500'
                        : 'border-gray-300 focus:ring-primary-dark/50 focus:border-primary-dark'
                    }`}
                    placeholder={t('contact.placeholders.enterMessage')}
                    maxLength="1000"
                  ></textarea>
                  <div className="flex justify-between items-start mt-1">
                    {errors.message && touched.message ? (
                      <p className="text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1.5 flex-shrink-0" /> {errors.message}
                      </p>
                    ) : (
                      <div></div>
                    )}
                    <p className="text-xs text-gray-500">
                      {formData.message.length}/1000
                    </p>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-primary-dark to-primary-mid text-white py-3.5 rounded-xl font-semibold hover:shadow-lg hover:shadow-primary-mid/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2 hover:-translate-y-0.5 active:translate-y-0 text-base"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>{t('contact.sending')}</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>{t('contact.send')}</span>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-4 sm:space-y-5">
              {/* Contact Details */}
              {contactInfo.map((info, index) => (
                <div key={index} className="bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-4 sm:p-5 border border-gray-100 hover:border-primary-light group">
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-primary-dark/10 to-primary-mid/10 rounded-lg sm:rounded-xl flex items-center justify-center text-primary-dark group-hover:scale-110 transition-transform duration-300">
                      {info.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm sm:text-base font-bold text-primary-dark mb-1 sm:mb-2 tracking-tight">
                        {info.title}
                      </h4>
                      <div className="space-y-0.5">
                        {info.details.map((detail, detailIndex) => (
                          <p key={detailIndex} className="text-xs sm:text-sm text-gray-600 leading-relaxed break-words">
                            {detail}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Full Width Map Section */}
          <div className="mt-8 sm:mt-10 lg:mt-12">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              <div className="w-full h-[250px] sm:h-[300px] md:h-[350px] lg:h-[420px]">
                <iframe
                  title="Dr. Haribhai Godhani Campus Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3691.5!2d70.45!3d21.52!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjHCsDMxJzEyLjAiTiA3MMKwMjcnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-full"
                ></iframe>
              </div>
              <div className="p-4 sm:p-5 bg-gradient-to-r from-gray-50 to-white border-t border-gray-100">
                <a
                  href="https://maps.app.goo.gl/dd3EwPLkxYxUw6vP6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto mx-auto sm:mx-0 bg-gradient-to-r from-primary-dark to-primary-mid text-white py-2.5 sm:py-3 px-6 sm:px-8 rounded-lg sm:rounded-xl hover:shadow-lg hover:shadow-primary-mid/30 transition-all duration-300 font-semibold inline-flex items-center justify-center hover:-translate-y-0.5 active:translate-y-0 text-xs sm:text-sm touch-target"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Get Directions
                </a>
              </div>
            </div>
          </div>

          {/* Quick Contact Cards */}
          <div className="mt-8 sm:mt-10 lg:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            <div className="bg-gradient-to-br from-primary-dark to-primary-mid rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white text-center shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-white/10">
              <div className="bg-white/20 w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 backdrop-blur-sm">
                <Phone className="w-6 h-6 sm:w-7 sm:h-7" />
              </div>
              <h4 className="font-bold mb-1 sm:mb-2 text-sm sm:text-base">{t('footer.emergencyContact')}</h4>
              <p className="text-xs sm:text-sm opacity-90 mb-1 sm:mb-2">{t('footer.available24x7')}</p>
              <a href="tel:+919427805272" className="font-semibold text-sm sm:text-base hover:underline">+91 94278 05272</a>
            </div>
            <div className="bg-gradient-to-br from-primary-dark to-primary-mid rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white text-center shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-white/10">
              <div className="bg-white/20 w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 backdrop-blur-sm">
                <Mail className="w-6 h-6 sm:w-7 sm:h-7" />
              </div>
              <h4 className="font-bold mb-1 sm:mb-2 text-sm sm:text-base">{t('footer.quickEmail')}</h4>
              <p className="text-xs sm:text-sm opacity-90 mb-1 sm:mb-2">{t('footer.fastResponse')}</p>
              <p className="font-semibold text-xs sm:text-sm break-all">info@sardarpateleducationtrust.org</p>
            </div>
            <div className="bg-gradient-to-br from-primary-dark to-primary-mid rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white text-center shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-white/10 sm:col-span-2 lg:col-span-1">
              <div className="bg-white/20 w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 backdrop-blur-sm">
                <Clock className="w-6 h-6 sm:w-7 sm:h-7" />
              </div>
              <h4 className="font-bold mb-1 sm:mb-2 text-sm sm:text-base">{t('footer.visitUs')}</h4>
              <p className="text-xs sm:text-sm opacity-90 mb-1 sm:mb-2">{t('footer.campusTourAvailable')}</p>
              <p className="font-semibold text-sm sm:text-base">{t('footer.visitingHours')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Contact

